import { useNavigation } from '@/app/hooks'
import { useParams } from 'react-router-dom'
import {
  scopedFleetAuthenticationCollection,
  useFetchEnergySystemRequiredScopes,
} from '@/reducers/teslaFleetAuthentication'
import { energySystemCollection } from '@/reducers/energySystems'
import { logEvent } from '@/logging'
import { getPermissionsEditUrl } from '../../verify-permissions/utils'
import { useUrlSearchParam } from '@/hooks'
import { useEffect, useState } from 'react'
import {
  DeviceAgnosticFleetAuthentication,
  TeslaFleetAuthenticationScope,
} from '@/types/teslaFleetAuthentication'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import { logMessage } from '@/logging/sentry'

export function useRefreshPermissionsIfNeeded(
  authenticationId: string,
  refetchAuthentications: () => void,
) {
  const [isLoading, setIsLoading] = useState(false)
  const { apiDispatch } = useApiDispatch()
  const [error, setError] = useState(false)
  const shouldRefresh =
    useUrlSearchParam('refresh_permissions') === 'true' && !error
  const { createAction: refreshPermissionsAction } =
    scopedFleetAuthenticationCollection.useCreate({
      path: `tesla-fleet-authentication/${authenticationId}/refresh`,
    })

  const sendRefreshRequest = async () => {
    setError(false)
    setIsLoading(true)
    // Remove the refresh_permissions param without a refresh
    const url = new URL(window.location.href)
    url.searchParams.delete('refresh_permissions')
    window.history.replaceState({}, '', url)
    // Request that permissions are refreshed
    const res = await apiDispatch<DeviceAgnosticFleetAuthentication>(
      refreshPermissionsAction(),
    )
    // refresh the authentications so we have the most up to date permissions
    await refetchAuthentications()
    setIsLoading(false)

    if (!res.ok) {
      logMessage(
        `VerifyPermissionsPage: Failed to refresh permissions, Err:  ${res.error}`,
      )
      logEvent(`capabilities_refreshed_permissions_failed`)
      setError(true)
    } else {
      logEvent(`capabilities_refreshed_permissions`)
    }
  }

  useEffect(() => {
    if (shouldRefresh && authenticationId) {
      sendRefreshRequest()
    }
  }, [shouldRefresh, authenticationId])

  return { isLoading, error }
}

export default function useViewModel() {
  const navigate = useNavigation()
  const { authenticationId } = useParams<{ authenticationId: string }>()
  const {
    data: scopedFleetAuthentications,
    isLoadingOrIdle: isLoadingScopedFleetAuthentications,
    error: scopedFleetAuthenticationsError,
    refetch: refetchScopedFleetAuthentications,
  } = scopedFleetAuthenticationCollection.useFetch(
    'tesla-fleet-authentication/list',
  )
  const authentication = scopedFleetAuthentications?.find(
    (authentication) => authentication.id === Number(authenticationId),
  )
  const {
    data: requiredScopes,
    isLoading: isLoadingRequiredScopes,
    error: requiredScopesError,
  } = useFetchEnergySystemRequiredScopes()
  const {
    data: energySystems,
    isLoadingOrIdle: isLoadingEnergySystems,
    error: energySystemsError,
    refetch: refetchEnergySystems,
  } = energySystemCollection.useFetch('energy-systems', {
    params: {
      authenticationId,
    },
  })

  const possibleScopes: TeslaFleetAuthenticationScope[] = [
    'user_data',
    'vehicle_device_data',
    'vehicle_location',
    'vehicle_cmds',
    'vehicle_charging_cmds',
    'energy_device_data',
    'energy_cmds',
  ]

  const missingScopes = possibleScopes.filter(
    (scope) => !authentication?.scopes.includes(scope),
  )

  const {
    isLoading: isLoadingPermissionRefresh,
    error: permissionRefreshError,
  } = useRefreshPermissionsIfNeeded(
    authenticationId,
    refetchScopedFleetAuthentications,
  )

  // Check if required permissions are missing
  const missingRequiredPermissions = missingScopes.some((scope) =>
    ['energy_device_data', 'energy_device_settings'].includes(scope),
  )

  const handleUpdatePermissions = () => {
    logEvent('verify_permissions_energy_system_opened_tesla_permissions')
    window.location.href = getPermissionsEditUrl()
  }

  const handleContinue = () => {
    if (energySystems.length > 0) {
      navigate.push(`/devices/energy-system/${energySystems[0].id}`)
    } else {
      navigate.goHome()
    }
  }

  const handleClose = () => {
    navigate.push('/connect-device/energy-system')
  }

  return {
    energySystems,
    isLoading:
      isLoadingScopedFleetAuthentications ||
      isLoadingRequiredScopes ||
      isLoadingEnergySystems ||
      isLoadingPermissionRefresh,
    permissionRefreshError,
    missingScopes,
    missingRequiredPermissions,
    handleUpdatePermissions,
    handleContinue,
    handleClose,
  }
}
